import { Box, Grid, Typography } from '@mui/material';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { DialogComponent } from '../../../components';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { OutlinedTextField } from '../../../components/InputFields';
import { useCommonMessageAndSpinnerHandlers } from '../../../hooks';
import { TwoFactorAuthSetUpData } from '../../../types/LogInTypes';
import API from '../../../utils/API';
import { NumberOf } from '../../../utils/commonUtils';
import { isNumberValid, isObjectValidAndNotEmpty, isTextValidAndNotEmpty } from '../../../utils/nullChecks';
import resolvePromise from '../../../utils/resolvePromise';
import useFetchTwoFactorAuthSetUpData from './dataFetchHooks/useFetchTwoFactorAuthSetUpData';

type Props = {
    open: boolean;
    onClose: () => void;
};

const TwoFactorAuthSetUpDialog = (props: Props) => {
    const { open, onClose } = props;
    const commonHandlers = useCommonMessageAndSpinnerHandlers();
    const { errorMessage } = commonHandlers;

    const [code, setCode] = useState('');

    const {
        data: twoFactorAuthSetUpDataResponse,
    } = useFetchTwoFactorAuthSetUpData();
    const twoFactorAuthSetUpData: TwoFactorAuthSetUpData | null =
        twoFactorAuthSetUpDataResponse?.data || null;

    const handleCodeChange = (value: string) => {
        setCode(value);
    };

    const enableTwoFactorAuth = async (twoFactorAuthCode: number, twoFactorAuthToken: string) => {
        const createSaleOrderPromise = axios.put(API.TWO_FACTOR_AUTH.ENABLE, {}, {
            params: {
                code: twoFactorAuthCode,
                twoFactorAuthToken,
            },
        });
        const [, error] = await resolvePromise(createSaleOrderPromise, commonHandlers, 'Enabled 2FA Successfully');
        if (!error) {
            onClose();
        }
    };

    const handleValidateClick = (twoFactorAuthToken: string) => {
        if (!isTextValidAndNotEmpty(code)) {
            errorMessage('Please enter the code');
            return;
        }
        const parsedCode = NumberOf(code);
        if (!isNumberValid(parsedCode) || parsedCode === 0) {
            errorMessage('Please enter a valid numeric code');
            return;
        }
        enableTwoFactorAuth(parsedCode, twoFactorAuthToken);
    };

    return (
        <DialogComponent
            title="Enable Two-Factor Authentication"
            open={open}
            onClose={onClose}
            disableRestoreFocus
        >
            {
                isObjectValidAndNotEmpty<TwoFactorAuthSetUpData>(twoFactorAuthSetUpData) && (
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <Typography variant="h6">
                                Scan the QR code
                            </Typography>
                            <Typography variant="body1">
                                Use your authenticator app to scan this QR code.
                            </Typography>
                        </Grid>
                        <Grid item sm={12} container justifyContent="center">
                            <Grid item>
                                <Box sx={{ border: 2 }} padding={2}>
                                    <QRCodeSVG
                                        value={twoFactorAuthSetUpData.qrUrl}
                                        size={256}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item sm={12}>
                            <Typography variant="body1" gutterBottom>
                                Or enter the code manually:
                            </Typography>
                            <Box sx={{ border: 1.2, borderColor: 'secondary.main' }} padding={1}>
                                <Typography variant="subtitle1">
                                    {twoFactorAuthSetUpData.secretKey}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item sm={12}>
                            <Typography variant="h6">
                                Enter the Code
                            </Typography>
                            <Typography variant="body1">
                                Enter the code generated by your authenticator app.
                            </Typography>
                        </Grid>
                        <Grid item container sm={12}>
                            <Grid item sm={6}>
                                <OutlinedTextField
                                    autoFocus
                                    value={code}
                                    onChange={handleCodeChange}
                                    label=""
                                    fullWidth
                                />
                            </Grid>
                            <Grid item sm={3} ml={2}>
                                <ActionButton
                                    variant="contained"
                                    onClick={() => {
                                        handleValidateClick(
                                            twoFactorAuthSetUpData.twoFactorAuthToken,
                                        );
                                    }}
                                    color="secondary"
                                >
                                    Enable
                                </ActionButton>
                            </Grid>
                        </Grid>
                    </Grid>
                )
            }
        </DialogComponent>
    );
};

export default TwoFactorAuthSetUpDialog;
